import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { apiRequest, API_ENDPOINTS, clearAuthData, getAuthToken, getCurrentUser } from '../../api/config';
import { useTheme } from './ThemeContext';
import { getTranslation } from './translations';

const DashboardLayout = () => {
  const { language, toggleLanguage, toggleTheme, darkMode } = useTheme();
  const t = getTranslation(language);
  const [collapsed, setCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showNotice, setShowNotice] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const langDropdownRef = useRef(null);
  const navigate = useNavigate();

  // 点击外部关闭语言下拉框
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setCurrentUser(null);
          return;
        }
        const user = getCurrentUser();
        if (!user || !user.email) {
          clearAuthData();
          setCurrentUser(null);
        } else {
          setCurrentUser(user);
        }
      } catch (error) {
        console.error("[DashboardLayout] 认证检查出错:", error);
        clearAuthData();
        setCurrentUser(null);
      }
    };
    checkAuth();

    // 检查剩余次数，非管理员且 <500 时显示提示
    const fetchUserProfile = async () => {
      try {
        const result = await apiRequest(API_ENDPOINTS.USER.PROFILE, {}, true, 'GET');
        if (result && (result.data || result)) {
          const profile = result.data || result;
          // 非管理员 且 剩余次数 < 500（0 表示无限次）
          if (
            profile.role !== 'admin' &&
            profile.role !== 'superadmin' &&
            profile.remainingCalls !== undefined &&
            profile.remainingCalls > 0 &&
            profile.remainingCalls < 500
          ) {
            setShowNotice(true);
          }
        }
      } catch (error) {
        console.error('[DashboardLayout] 获取用户信息失败:', error);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileOpen(!mobileOpen);
    } else {
      const newCollapsedState = !collapsed;
      setCollapsed(newCollapsedState);
      localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
    }
  };

  const handleLogout = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        clearAuthData();
        setCurrentUser(null);
        navigate('/dashboard/login');
        return;
      }
      await apiRequest(API_ENDPOINTS.USER.LOGOUT, {
        method: 'POST'
      });
      clearAuthData();
      setCurrentUser(null);
      navigate('/dashboard/login');
    } catch (error) {
      console.error('退出登录失败:', error);
      clearAuthData();
      setCurrentUser(null);
      navigate('/dashboard/login');
    }
  };

  const getUserInitials = (email) => {
    if (!email) return '?';
    const username = email.split('@')[0];
    if (username.includes('.') || username.includes('_')) {
      const parts = username.split(/[._]/);
      return parts.map(part => part[0]?.toUpperCase() || '').join('').substring(0, 2);
    }
    return username.substring(0, 2).toUpperCase();
  };

  return (
    <div className="app">
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          style={{ display: 'none' }}
        />
      )}
      
      <Sidebar 
        collapsed={collapsed} 
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        currentUser={currentUser}
        onLogout={handleLogout}
        onToggle={toggleSidebar}
      />
      
      <div className="main">
        {/* 顶部通知横幅 */}
        {showNotice && (
          <div className="notice-banner" id="noticeBanner">
            <span>{t('lowCallsNotice')}</span>
            <a href="#" onClick={(e) => { e.preventDefault(); setShowNotice(false); }} className="notice-close">✕</a>
          </div>
        )}
        
        <div className="topbar">
          <div className="topbar-breadcrumb" onClick={() => window.location.href = '/'} style={{cursor: 'pointer'}}>
            {t('brandName')} / <span className="current">{t('adminDashboardTitle')}</span>
          </div>
          <div className="topbar-actions">
            <button className="tb-btn" title={t('notifications')}>
              🔔
            </button>
            <button className="tb-btn theme-toggle" onClick={toggleTheme} title={darkMode ? '浅色模式' : '深色模式'}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <div className="lang-selector" ref={langDropdownRef}>
              <button
                className="tb-btn lang-toggle"
                onClick={() => setShowLangDropdown(!showLangDropdown)}
              >
                🌐 {language === 'zh' ? t('simplifiedChinese') : t('english')}
              </button>
              {showLangDropdown && (
                <div className="lang-dropdown">
                  <div
                    className={`lang-option ${language === 'zh' ? 'active' : ''}`}
                    onClick={() => { if (language !== 'zh') toggleLanguage(); setShowLangDropdown(false); }}
                  >
                    {t('simplifiedChinese')}
                  </div>
                  <div
                    className={`lang-option ${language === 'en' ? 'active' : ''}`}
                    onClick={() => { if (language !== 'en') toggleLanguage(); setShowLangDropdown(false); }}
                  >
                    {t('english')}
                  </div>
                </div>
              )}
            </div>
            <button className="tb-btn">
              <div className="tb-avatar">
                {currentUser ? getUserInitials(currentUser.email) : 'U'}
              </div>
              <span className="tb-username">
                {currentUser ? currentUser.email : t('user')}
              </span>
            </button>
          </div>
        </div>
        
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout; 